import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { TABLES } from "../../../Apis/Tables";
import DropDownMenuItem from "../../../components/DropDown/DropDownMenuItem";
import DropDownMenuProfile from "../../../components/DropDown/DropDownMenuProfile";
import { DetailsIconsOptions, IconMoreVerticalDots } from "../../../components/buttons/IconButtons";
import TableFunction from "../../../components/table/TableFunction";
import { SEARCH_MODEL } from "../../../components/table/Model/TableModel";
import { VIEW_TYPE } from "../../../enums/ViewType";
import { SimpleSearchWithId } from "../../../reduxToolkit/Search/SearchApi";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Units logic of the building detail: paginated search of the apartments of the
 * building, per card actions menu and the details modal opened from it.
 *
 * @param {Object} props
 * @param {string|number} props.buildingId - Building the units belong to.
 * @returns {Object} Localization function, list state and the card handlers.
 */
export const UnitsListApp = ({ buildingId }) => {
    const dispatch = useDispatch();
    const option = TABLES.BUILDING_APARTMENT;
    const [searchElement, setSearchElement] = useState("");
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const [modified, setModified] = useState(false);
    // Flags SearchSimple works with, the search itself runs through getData
    const [search, setSearch] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [bodyForExport, setBodyForExport] = useState('');
    const [detailsModal, setDetailsModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    // Building the last fetch ran with, the detail is not loaded yet on mount
    const requestedBuildingId = useRef(null);
    // Scrollable container of the grid, panelTables-container sizes it with --top-value
    const elementRef = useRef(null);

    /**
     * Loads the units page the grid renders, the apartments of the building are
     * searched through the BUILDING_APARTMENT model of the simple search by id,
     * which takes the building as the userId of the body.
     *
     * @param {number} page - Page requested by the pagination, zero based.
     * @param {Object} [options] - Extra values sent by TableFunction.
     * @param {number} [options.rowsPerPage] - Page size in use.
     * @param {string} [options.searchElement] - Search value the fetch runs with.
     */
    const getData = (page, options) => {
        // The building is required by the search, on the first render of the
        // detail it is still loading and the fetch is left to the effect below
        if (!buildingId) return;

        requestedBuildingId.current = buildingId;

        const body = SEARCH_MODEL.setObject({
            page,
            size: options?.rowsPerPage || rowsPerPage,
            model: option,
            key: options?.searchElement ?? searchElement,
            userId: buildingId
        });

        setBodyForExport(body);

        dispatch(SimpleSearchWithId({ body })).then(
            ({ payload: { data: { data, success } } }) => {
                if (success !== true) return;

                setResults({
                    ...data,
                    content: data?.content || []
                });
            }
        );
    };

    // Use TableFunction to handle the grid logic (pagination, actions menu)
    const {
        t,
        pagination,
        rowsPerPage,
        openMenu,
        handleMenuToggle,
        handleClose
    } = TableFunction({
        data: results,
        labelRowsPerPage: 'units_per_page',
        hasContainer: true,
        fixedPagination: false,
        view: VIEW_TYPE.GRID.name,
        searchElement,
        getData,
        modified,
        setModified
    });

    // Loads the list once the building detail arrives, the mount fetch of
    // TableFunction is skipped while there is no building to search with
    useEffect(() => {
        if (!buildingId || requestedBuildingId.current === buildingId) return;

        setModified(true);
    }, [buildingId]);

    /**
     * Distance from the top of the viewport to the grid, panelTables-container
     * takes the rest of the screen with it and leaves the pagination outside
     * of the scroll.
     */
    useLayoutEffect(() => {
        if (!elementRef?.current) return;

        const distanceTop = elementRef.current.getBoundingClientRect().top + 90;
        elementRef.current.style.setProperty('--top-value', `${distanceTop}px`);
    }, [results?.content]);

    /**
     * Search of SearchSimple, the units need the building on the body so the
     * fetch is the same getData of the grid instead of the generic one.
     */
    const handleSearch = () => getData(0);

    /**
     * Reset of SearchSimple, brings the first page back without the search key.
     */
    const handleResetSearch = () => getData(0, { searchElement: "" });

    /**
     * Closes the details modal and clears the unit it was showing.
     */
    const hideDetails = () => {
        setDetailsModal(false);
        setSelectedItem(null);
    };

    /**
     * Vertical three dots menu with the actions available for one unit.
     *
     * @param {Object} item - Unit data.
     * @param {number} index - Card position, used for the menu item ids.
     * @returns {JSX.Element}
     */
    const getActionsButtons = (item, index) => {
        return (
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item?.id}`}
                icon={<IconMoreVerticalDots />}
            >
                <DropDownMenuItem
                    id={`btn_unl_details_${item?.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={'details'}
                    icon={<DetailsIconsOptions/>}
                    click={() => {
                        setSelectedItem(item);
                        setDetailsModal(true);
                    }}
                />
            </DropDownMenuProfile>
        );
    };

    return {
        t,
        option,
        searchElement,
        setSearchElement,
        results,
        setResults,
        rowsPerPage,
        search,
        setSearch,
        simpleSearch,
        setSimpleSearch,
        bodyForExport,
        setBodyForExport,
        handleSearch,
        handleResetSearch,
        detailsModal,
        selectedItem,
        hideDetails,
        getActionsButtons,
        pagination,
        elementRef,
    };
};
